import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import type { IActiveDeliveryStats } from "../../apis/delivery"
import { KpiRow } from "../delivery/atoms"

interface Props {
  /** Business-wide counts from POST /delivery_note/active-stats (O105-O107); undefined while loading. */
  stats: IActiveDeliveryStats | undefined
  availableVehicles: { ready: number; total: number; onRoute: number; maintenance: number }
}

/** Идэвхтэй KPI row (canvas Ord_DeliveryTabsA). Every number is server-wide, not the current page. */
export function ActiveStatsCards({ stats, availableVehicles }: Props) {
  const { t } = useTranslation("delivery")
  const n = (v: number | undefined) => (v == null ? "-" : v)
  const def = stats?.definitions
  return (
    <KpiRow
      items={[
        { key: "active", label: t("active.stats.active"), value: n(stats?.active), meta: t("manage.active.activeMeta") },
        {
          key: "unassigned",
          label: t("active.stats.noDriver"),
          value: n(stats?.unassigned),
          meta: (stats?.unassigned ?? 0) > 0 ? t("active.stats.needAssign") : t("active.stats.allAssigned"),
        },
        {
          key: "live",
          tone: "green",
          label: t("active.stats.onRoute"),
          value: n(stats?.live),
          meta: stats?.lastGpsAt
            ? t("manage.active.lastGps", { at: dayjs(stats.lastGpsAt).format("YYYY-MM-DD HH:mm") })
            : t("manage.active.liveMeta", { min: def?.liveWindowMinutes ?? 30 }),
        },
        {
          key: "sla",
          tone: (stats?.slaRisk ?? 0) > 0 ? "red" : "grey",
          label: t("active.stats.slaRisk"),
          value: n(stats?.slaRisk),
          meta: t("manage.active.slaMeta", { h: def?.slaRiskHours ?? 4 }),
        },
        {
          key: "delivered",
          label: t("active.stats.deliveredToday"),
          value: n(stats?.deliveredToday),
          meta: t("manage.active.deliveredMeta"),
        },
        {
          key: "vehicles",
          label: t("active.stats.readyVehicles"),
          value: availableVehicles.ready,
          suffix: `/ ${availableVehicles.total}`,
          meta: t("active.stats.vehicleMeta", {
            onRoute: availableVehicles.onRoute,
            maintenance: availableVehicles.maintenance,
          }),
        },
      ]}
    />
  )
}
